import { useState, useEffect } from 'react';
import { apiJsonFetch } from '../utils/api';
import {
  Plus,
  Truck,
  Upload,
  Clock,
  CheckCircle,
  Trash2,
  X,
  AlertCircle,
} from 'lucide-react';

interface Carrier {
  id: string;
  carrierType: string;
  carrierName: string;
  isActive: boolean;
}

interface ShippingLabel {
  id: string;
  trackingNumber: string;
  printedAt: string;
  status: string;
  order?: {
    mallOrderId: string;
  };
  carrier?: {
    carrierName: string;
  };
}

const carrierTypes = [
  { value: 'yamato', label: 'ヤマト運輸', icon: '🚚' },
  { value: 'sagawa', label: '佐川急便', icon: '📦' },
  { value: 'japanpost', label: '日本郵便', icon: '📮' },
  { value: 'other', label: 'その他', icon: '🚛' },
];

export default function ShippingLabelsPage() {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [shippingLabels, setShippingLabels] = useState<ShippingLabel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddCarrierModal, setShowAddCarrierModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'carriers' | 'history'>('carriers');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [carriersRes, historyRes] = await Promise.all([
        apiJsonFetch('/api/shipping-labels/carriers'),
        apiJsonFetch('/api/shipping-labels/history'),
      ]);
      if (carriersRes.ok) {
        setCarriers(carriersRes.data.carriers || []);
      }
      if (historyRes.ok) {
        setShippingLabels(historyRes.data.shippingLabels || []);
      }
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCarrier = async (carrierId: string) => {
    if (!confirm('この配送業者を削除しますか？')) return;
    try {
      await apiJsonFetch(`/api/shipping-labels/carriers/${carrierId}`, {
        method: 'DELETE',
      });
      fetchData();
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">送り状発行連携</h1>
          <p className="text-sm text-gray-500 mt-1">配送業者との連携・送り状発行</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAddCarrierModal(true)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            配送業者追加
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload className="h-5 w-5" />
            伝票番号取込
          </button>
        </div>
      </div>

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {[
            { id: 'carriers', label: '配送業者', icon: Truck },
            { id: 'history', label: '発行履歴', icon: Clock },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 pb-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">読み込み中...</p>
        </div>
      ) : activeTab === 'carriers' ? (
        /* 配送業者一覧 */
        carriers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <Truck className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="text-gray-500 mt-2">配送業者が設定されていません</p>
            <button
              onClick={() => setShowAddCarrierModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
            >
              配送業者を追加する
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {carriers.map((carrier) => {
              const carrierInfo = carrierTypes.find((c) => c.value === carrier.carrierType);
              return (
                <div
                  key={carrier.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
                        {carrierInfo?.icon || '🚚'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{carrier.carrierName}</h3>
                        <p className="text-sm text-gray-500">
                          {carrierInfo?.label || carrier.carrierType}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        carrier.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {carrier.isActive ? (
                        <>
                          <CheckCircle className="h-3 w-3" />
                          有効
                        </>
                      ) : (
                        '無効'
                      )}
                    </span>
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <div className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-amber-700 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="h-4 w-4" />
                      送り状発行API連携は準備中
                    </div>
                    <button
                      onClick={() => handleDeleteCarrier(carrier.id)}
                      className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="削除"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* 発行履歴 */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {shippingLabels.length === 0 ? (
            <div className="p-8 text-center">
              <Clock className="h-12 w-12 text-gray-300 mx-auto" />
              <p className="text-gray-500 mt-2">発行履歴がありません</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    伝票番号
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    受注番号
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    配送業者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    発行日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shippingLabels.map((label) => (
                  <tr key={label.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900">
                      {label.trackingNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {label.order?.mallOrderId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {label.carrier?.carrierName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {label.printedAt
                        ? new Date(label.printedAt).toLocaleString('ja-JP')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          label.status === 'printed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {label.status === 'printed' ? '発行済み' : label.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 配送業者追加モーダル */}
      {showAddCarrierModal && (
        <AddCarrierModal
          onClose={() => setShowAddCarrierModal(false)}
          onSuccess={() => {
            setShowAddCarrierModal(false);
            fetchData();
          }}
        />
      )}

      {/* 伝票番号取込モーダル */}
      {showImportModal && (
        <ImportTrackingModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

// 配送業者追加モーダル
function AddCarrierModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    carrierType: 'yamato',
    carrierName: '',
    customerId: '',
    apiKey: '',
    password: '',
    defaultDeliveryType: '',
    defaultTemperatureType: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await apiJsonFetch('/api/shipping-labels/carriers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">配送業者追加</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">配送業者タイプ *</label>
              <select
                value={formData.carrierType}
                onChange={(e) => setFormData({ ...formData, carrierType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {carrierTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">業者名 *</label>
              <input
                type="text"
                value={formData.carrierName}
                onChange={(e) => setFormData({ ...formData, carrierName: e.target.value })}
                required
                placeholder="例: ヤマト運輸（宅急便）"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">お客様コード</label>
              <input
                type="text"
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                placeholder="配送業者から発行されたコード"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">APIキー</label>
                <input
                  type="password"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">パスワード</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">デフォルト配送種別</label>
                <input
                  type="text"
                  value={formData.defaultDeliveryType}
                  onChange={(e) => setFormData({ ...formData, defaultDeliveryType: e.target.value })}
                  placeholder="例: 宅急便"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">温度帯</label>
                <select
                  value={formData.defaultTemperatureType}
                  onChange={(e) => setFormData({ ...formData, defaultTemperatureType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">指定なし</option>
                  <option value="normal">常温</option>
                  <option value="cool">冷蔵</option>
                  <option value="frozen">冷凍</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                キャンセル
              </button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? '追加中...' : '追加'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// 伝票番号取込モーダル
function ImportTrackingModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ success: number; error: number; errors: string[] } | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setError('ファイルを選択してください');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      // CSVファイルを読み込んでパース
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        throw new Error('CSVファイルが空です');
      }

      // ヘッダー行を解析
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const mallOrderIdIndex = headers.findIndex(h => h.includes('注文番号') || h.includes('order') || h.includes('受注'));
      const trackingNumberIndex = headers.findIndex(h => h.includes('伝票') || h.includes('tracking') || h.includes('送り状'));

      if (trackingNumberIndex === -1) {
        throw new Error('伝票番号の列が見つかりません。ヘッダーに「伝票番号」または「tracking」を含めてください。');
      }

      // データ行をパース
      const trackingData = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/"/g, ''));
        return {
          mallOrderId: mallOrderIdIndex >= 0 ? cols[mallOrderIdIndex] : undefined,
          trackingNumber: cols[trackingNumberIndex],
        };
      }).filter(item => item.trackingNumber);

      if (trackingData.length === 0) {
        throw new Error('有効なデータがありません');
      }

      // APIに送信
      const res = await apiJsonFetch('/api/shipping-labels/import-tracking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackingData }),
      });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');

      setResult(res.data.result);

      if (res.data.result.success > 0) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">伝票番号取込</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm whitespace-pre-wrap">{error}</span>
            </div>
          )}

          {result && (
            <div className={`mb-4 p-3 rounded-lg border ${result.error > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm font-medium ${result.error > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                取込完了: {result.success}件成功 / {result.error}件エラー
              </p>
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-2 text-xs text-yellow-700 list-disc list-inside">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CSVファイル *</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-1">CSVファイル形式:</p>
                <p className="text-xs text-gray-500">
                  ヘッダー行: 注文番号, 伝票番号<br />
                  例: ORD-001, 1234-5678-9012
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                {result ? '閉じる' : 'キャンセル'}
              </button>
              {!result && (
                <button type="submit" disabled={loading || !file} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {loading ? '取込中...' : '取込開始'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

